import type { AxiosResponse } from 'axios'
import { isAxiosError } from 'axios'
import type { Store, StoreEnhancer } from 'redux'
import { applyMiddleware, compose, legacy_createStore as createStore } from 'redux'
import createSagaMiddleware from 'redux-saga'

import { requestVersionCheck } from '@libs/typescript-utils'

import axiosInstance from './api'
import checkTokenMiddleware from './middleware'
import rootReducer, { type AllAction } from './reducers'
import rootSaga from './sagas'

const sagaMiddleware = createSagaMiddleware()
const middlewareEnhancer = applyMiddleware(sagaMiddleware, checkTokenMiddleware)
const enhancers: StoreEnhancer[] = [middlewareEnhancer]

if (process.env.NODE_ENV === 'development') {
  const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION__

  if (typeof devToolsExtension === 'function') {
    enhancers.push(devToolsExtension())
  }
}

const composedEnhancers = enhancers.reduce((acc, curr) => compose(acc, curr))

const store = createStore(rootReducer, composedEnhancers) as Store<RootStore, AllAction>

function checkVersionOnMismatch(headers: AxiosResponse['headers'] | undefined) {
  if (headers?.['app-version-mismatch']) {
    void requestVersionCheck()
  }
}

// The backend flags a version mismatch on error responses too.
axiosInstance.interceptors.response.use(
  response => {
    checkVersionOnMismatch(response.headers)
    return response
  },
  (error: unknown) => {
    if (isAxiosError(error)) {
      checkVersionOnMismatch(error.response?.headers)
    }
    throw error
  }
)

sagaMiddleware.run(rootSaga)

export default store
